/* global colorSwatches */

// Constants for elements and attributes
const elements = {
  totalPages: 'input[data-total-pages]',
  currentPage: 'input[data-current-grid-page]',
  nextUrl: '[data-next-url]',
  loadMoreGrid: '[data-collection-filters-el="item-container"]',
  infiniteScroll: '.js-infinite-scroll',
  loadMore: '.js-load-more',
  scrollData: '.infinite-scroll__data',
  pagination: '[data-collection-filters-el="pagination"]',
}

const getElement = (selector) => document.querySelector(selector)
const getElements = (selector) => document.querySelectorAll(selector)
const getValue = (selector) => getElement(selector)?.value
const getNumberValue = (selector) => Number.parseInt(getValue(selector), 10)

const saveGridState = () => {
  try {
    const gridContent = getElement(elements.loadMoreGrid)?.innerHTML
    const lastGridPage = getValue(elements.currentPage)

    if (!gridContent || !lastGridPage) {
      console.warn('Invalid grid state to save')
      return
    }

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('gridContent', gridContent)
      sessionStorage.setItem('lastGridPage', lastGridPage)
    }
  } catch (error) {
    console.error('Failed to save grid state:', error)
  }
}

const updatePageState = (currentPage) => {
  getElement(elements.currentPage).value = currentPage
}

const fetchNewContent = async (url) => {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`fetchNewContent error: status: ${response.status}`)
    }
    const html = await response.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const newContent = doc.querySelector(elements.loadMoreGrid)?.innerHTML
    const pagination = doc.querySelector(elements.pagination)?.outerHTML

    if (!newContent) {
      throw new Error('fetchNewContent: No content found in response')
    }

    return { newContent, pagination }
  } catch (error) {
    console.error('fetchNewContent error:', error)
    throw error
  }
}

const attachProductClickHandler = (item) => {
  if (!item._hasClickHandler) {
    item.addEventListener('click', (e) => {
      sessionStorage.setItem(
        'returnProductId',
        e.currentTarget.dataset.productId,
      )
    })
    item._hasClickHandler = true
  }
}

const attachProductClickHandlers = () => {
  getElements('[data-product-grid-item]').forEach(attachProductClickHandler)
}

const updateScrollData = (scrollData, nextUrl, currentPage) => {
  const nextUrlInput = scrollData.querySelector(elements.nextUrl)
  const currentPageInput = scrollData.querySelector(elements.currentPage)

  if (nextUrlInput) {
    nextUrlInput.dataset.nextUrl = nextUrl
    nextUrlInput.value = nextUrl
  }
  if (currentPageInput) {
    currentPageInput.dataset.currentPage = currentPage
    currentPageInput.value = currentPage
  }
}

const updateButtonState = (button, isLoading) => {
  if (isLoading) {
    button.setAttribute('disabled', '')
    button.classList.add('loading')
  } else {
    button.removeAttribute('disabled')
    button.classList.remove('loading')
  }
}

const loadItems = async (button) => {
  const totalPages = getNumberValue(elements.totalPages)
  const currentPage = getNumberValue(elements.currentPage)

  if (currentPage >= totalPages) {
    for (const spinner of getElements(elements.infiniteScroll)) {
      spinner.setAttribute('aria-hidden', 'true')
    }

    return
  }

  const nextPage = currentPage + 1
  const newURL = new URL(getValue(elements.nextUrl), window.location.origin)
  const newURLSearchParams = new URLSearchParams(newURL.search)

  newURLSearchParams.set('page', nextPage)
  newURL.search = newURLSearchParams.toString()

  const nextUrl = newURL.toString()

  updatePageState(nextPage)
  updateButtonState(button, true)

  try {
    const { newContent, pagination } = await fetchNewContent(nextUrl)
    if (!newContent) return

    const newPagination = new DOMParser().parseFromString(
      pagination,
      'text/html',
    )
    const newNextUrl = newPagination?.querySelector(elements.nextUrl)?.value

    if (newNextUrl) {
      getElements(elements.nextUrl).forEach((element) => {
        element.value = newNextUrl
      })
    }

    getElement(elements.loadMoreGrid).insertAdjacentHTML(
      'beforeend',
      newContent,
    )
    attachProductClickHandlers()
    saveGridState()

    if (typeof colorSwatches === 'function') {
      colorSwatches()
    }

    if (nextPage <= totalPages) {
      const scrollData = getElement(elements.scrollData)

      if (scrollData && nextPage !== totalPages) {
        const scrollUrl = newNextUrl || nextUrl
        updateScrollData(scrollData, scrollUrl, nextPage)
        checkVisibility()
      }

      if (nextPage === totalPages) {
        button.remove()
      } else {
        updateButtonState(button, false)
      }
    }
  } catch (error) {
    console.error('Error loading more items:', error)
    updateButtonState(button, false)
  }
}

const createIntersectionObserver = () => {
  // eslint-disable-next-line compat/compat
  return new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target)
          loadItems(entry.target)
        }
      }
    },
    {
      rootMargin: '100px', // Load slightly before element comes into view
    },
  )
}

const checkVisibility = () => {
  const observer = createIntersectionObserver()

  for (const spinner of getElements(elements.infiniteScroll)) {
    observer.observe(spinner)
  }
}

const restoreGridState = () => {
  const savedContent = sessionStorage.getItem('gridContent')
  const lastPage = sessionStorage.getItem('lastGridPage')
  const returnProductId = sessionStorage.getItem('returnProductId')

  // Check if the saved content is valid
  if (!(savedContent && lastPage && returnProductId)) return false

  // Clear the session storage to avoid using outdated data
  sessionStorage.removeItem('gridContent')
  sessionStorage.removeItem('lastGridPage')
  sessionStorage.removeItem('returnProductId')

  getElement(elements.loadMoreGrid).innerHTML = savedContent
  getElement(elements.currentPage).value = lastPage

  const productElement = getElement(`[data-product-id="${returnProductId}"]`)
  if (productElement) {
    productElement.scrollIntoView({ behavior: 'instant' })

    setTimeout(() => {
      checkVisibility()
    }, 0)
  }

  if (typeof colorSwatches === 'function') {
    colorSwatches()
  }

  return true
}

const initLoadMore = () => {
  window.scrollTo({ top: 0, behavior: 'instant' })
}
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual'
}

document.addEventListener('DOMContentLoaded', () => {
  if (!restoreGridState() && !window.location.hash) {
    initLoadMore()
  }
  attachProductClickHandlers()
  checkVisibility()

  for (const button of getElements(elements.loadMore)) {
    button.addEventListener('click', () => loadItems(button))
  }
})

// New filters = new grid starting at page 1, go to top
document.addEventListener('productGrid:updated', () => {
  ['gridContent', 'lastGridPage', 'returnProductId'].forEach((i) =>
    sessionStorage.removeItem(i),
  )
  attachProductClickHandlers()
  checkVisibility()
  initLoadMore()
})
